import { useTranslation } from "react-i18next";
import type { ImportedSource, SourceUpload } from "../../../api/content/hostingApi";
import { shortReference } from "../../../shared/references/shortReferences";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import ImportRunStatus from "./ImportRunStatus";

/** What an import through a source did: the commit that arrived, and the Thread that runs its import Workflow. */
export type SourceImportOutcome =
  | { readonly kind: "upload"; readonly corpusName: string; readonly upload: SourceUpload }
  | { readonly kind: "cloud"; readonly corpusName: string; readonly source: ImportedSource; readonly firstFetch: "fetched" | "unchanged" | "failed" };

export default function SourceImportSummary({ projectId, outcome, onStarted }: { projectId: string; outcome: SourceImportOutcome; onStarted: (threadId: string) => void }) {
  const { t } = useTranslation();
  if (outcome.kind === "upload") {
    const { upload } = outcome;
    return (
      <StatusMessage tone="success" title={t("sources.import.result.uploadTitle")}>
        {upload.commit === null ? (
          <p>{t("sources.import.result.uploadUnchanged", { source: upload.sourceName })}</p>
        ) : (
          <>
            <p>{t("sources.import.result.uploaded", { count: upload.fileCount, commit: shortReference("gitObject", upload.commit), source: upload.sourceName, corpus: outcome.corpusName })}</p>
            <ImportRunStatus projectId={projectId} sourceId={upload.sourceId} commit={upload.commit} onStarted={onStarted} />
          </>
        )}
      </StatusMessage>
    );
  }
  return (
    <StatusMessage tone={outcome.firstFetch === "failed" ? "warning" : "success"} title={t("sources.import.result.connectedTitle")}>
      <p>{t("sources.import.result.connected", { source: outcome.source.sourceName, corpus: outcome.corpusName })}</p>
      <p>{t(`sources.import.result.firstFetch.${outcome.firstFetch}`)}</p>
      {outcome.firstFetch === "fetched" ? <ImportRunStatus projectId={projectId} sourceId={outcome.source.sourceId} commit={null} onStarted={onStarted} /> : null}
    </StatusMessage>
  );
}
